import { html, TemplateResult } from "lit-html";
import classNames from "classnames";
import { useLayoutEffect, useRef } from "haunted";
import { useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import { ref } from "Components/directives/ref";
import { HauntedFunc } from "Shared/haunted/HooksHelpers";
import { useWindowSize } from "Shared/haunted/useWindowSize";

export interface Props {
	anchorElement?: HTMLElement;
	content: TemplateResult;
	fixedDown?: boolean;
	isOpen: boolean;
	isOutbound: boolean;
	modalOnMobile?: boolean;
	useParentWidth: boolean;
}

export interface PositionInfo {
	x: number;
	y: number;
	width: number;
}

export const name = "ac-dropdown2";
export const useShadowDOM = false;

export const Component: HauntedFunc<Props> = (host) => {
	const props: Props = {
		anchorElement: host.anchorElement,
		content: host.content,
		fixedDown: host.fixedDown,
		isOpen: host.isOpen,
		isOutbound: host.isOutbound,
		modalOnMobile: host.modalOnMobile,
		useParentWidth: host.useParentWidth,
	};

	const root = useRef<HTMLDivElement>(null);
	const content = useRef<HTMLDivElement>(null);

	const windowSize = useWindowSize();

	const [coordinates, setCoordinates] = useState<PositionInfo>({ x: 0, y: 0, width: 0 });

	const isElemInvisible = (elem: HTMLElement) =>
		!elem.getBoundingClientRect().width || !elem.getBoundingClientRect().height;

	const shouldMoveUpInDom = (elem: HTMLElement) => {
		return isElemInvisible(elem) && elem.parentElement;
	};

	const firstVisibleParent = (elem?: HTMLElement) => {
		let parent = elem?.parentElement;

		if (!parent) return undefined;

		while (shouldMoveUpInDom(parent)) {
			parent = parent.parentElement;
		}

		return parent || undefined;
	};

	const getContextCoordinates = (elem: HTMLElement): PositionInfo => {
		if (!elem.parentElement) return { x: 0, y: 0, width: 0 };

		let contextElem = elem.parentElement;
		let x = 0;
		let y = 0;

		while (contextElem) {
			if (getComputedStyle(contextElem).transform !== "none") {
				const clientRect = contextElem.getBoundingClientRect();
				x += clientRect.left;
				y += clientRect.top;
			}

			contextElem = contextElem.parentElement;
		}

		return { x, y, width: null };
	};

	const calculatePosition = (elem?: HTMLElement): PositionInfo => {
		if (!elem) return { x: 0, y: 0, width: 0 };

		const clientRect = elem.getBoundingClientRect();
		const contextElement = getContextCoordinates(elem);

		return {
			x: clientRect.left - contextElement.x,
			y: clientRect.top - contextElement.y,
			width: clientRect.width,
		};
	};

    const getCoordinates = (element: HTMLElement | null): PositionInfo => {
		const parent = firstVisibleParent(element || undefined);
		if (!parent) return { x: 0, y: 0, width: 0 };

		const clientRect = parent.getBoundingClientRect();
		const coords = calculatePosition(parent);
		const contentWidth = content.current?.scrollWidth || 0;
		const viewportWidth = window.innerWidth;

		let x = coords.x;

		// DEVNOTE: If the dropdown content is wider than the viewport, we adjust its position towards the left
		if (x + contentWidth > viewportWidth) {
			x = viewportWidth - contentWidth - 24;
		}

		// DEVNOTE: If the dropdown content is pushed out of view on the left, we reset its position to the left edge
		if (x < 0) x = 0;

		return {
			x,
			y: coords.y + clientRect.height,
			width: coords.width,
		};
	};

	const recalculate = () => {
		const handleResize = () => {
			window.setTimeout(() => {
				if ((props.anchorElement || root.current?.parentElement) && windowSize.width) {
					setCoordinates(getCoordinates(props.anchorElement || root.current.parentElement));
				}
			}, 0);
		};

		handleResize();
		window.addEventListener("resize", handleResize);
		window.addEventListener("scroll", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("scroll", handleResize);
		};
	};

	useEffectWithoutCleanup(recalculate, [windowSize.height, windowSize.width, props.isOpen]);

	useLayoutEffect(() => {
		if (props.anchorElement || root.current?.parentElement) {
			window.setTimeout(() => {
				const coords = getCoordinates(props.anchorElement || root.current.parentElement);
				setCoordinates(coords);
			}, 0);
		}
	}, [props.content]);

	const fitsVertically = () =>
		coordinates.y + (content.current?.scrollHeight || 0) - window.scrollY <= window.innerHeight;

	const top = () =>
		fitsVertically() || props.fixedDown ? coordinates.y : coordinates.y - (content.current?.scrollHeight || 0) - 58;

	const toPortal = () => html`
		<div
			style=${`top: ${top() + 8}px; left: ${coordinates.x}px;` +
			(props.useParentWidth ? `width: ${coordinates.width}px;` : "")}
			class=${classNames(
				"fixed min-w-52 select-none overflow-hidden rounded-lg shadow-select",
				"[&_*]:select-none",
				{
					"-z-10 opacity-0 h-0": !props.isOpen,
					"z-[9999] opacity-100": props.isOpen,
				}
			)}
			@click=${(e: MouseEvent) => e.stopPropagation()}
		>
			<div ref=${ref(content)}>${props.content}</div>
		</div>
	`;

	const regularTemplate = () => html`<div ref=${ref(root)}>${toPortal()}</div>`;

	const mobileModalTemplate = () =>
		props.isOpen
			? html` <div ref=${ref(root)} class="fixed inset-0 z-[1000000] bg-black/50">
					<div
						ref=${ref(content)}
						class="absolute left-1/2 top-1/2 max-h-[95vh] max-w-[95vw] -translate-x-1/2 -translate-y-1/2 transform"
					>
						${props.content}
					</div>
			  </div>`
			: html``;

	return props.modalOnMobile && windowSize.size === "XS" ? mobileModalTemplate() : regularTemplate();
};
